import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  GoogleLogo, ArrowRight, Lock, EnvelopeSimple, User, Eye, EyeSlash,
} from "@phosphor-icons/react";
import { api } from "../../lib/api";

export default function AuthForm({ mode, setMode, onSubmit, busy, err, googleLogin, initialRef }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [name, setName] = useState("");
  const [referralCode, setReferralCode] = useState((initialRef || "").toUpperCase());
  const [referralValid, setReferralValid] = useState(null);
  const [referrerName, setReferrerName] = useState("");

  useEffect(() => {
    if (!referralCode || referralCode.length < 4) {
      setReferralValid(null);
      setReferrerName("");
      return;
    }
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/referral/validate", { code: referralCode });
        setReferralValid(!!data.valid);
        setReferrerName(data.referrer_name || "");
      } catch {
        setReferralValid(false);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [referralCode]);

  const submit = (e) => {
    e.preventDefault();
    onSubmit({ email, password, name, referralCode });
  };

  return (
    <div className="flex items-center justify-center px-6 lg:px-12 py-10 lg:py-14 border-t lg:border-t-0 lg:border-l border-subtle bg-surface/40 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.45, delay: 0.1 }}
        className="w-full max-w-sm"
        data-testid="auth-card"
      >
        <div className="mb-8">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em] text-muted-fg mb-2">
            /// {mode === "login" ? "Sign in" : "Create account"}
          </div>
          <h2 className="text-3xl font-medium tracking-tight text-primary-fg">
            {mode === "login" ? "Welcome back" : "Join the scanner"}
          </h2>
          <p className="text-sm text-secondary-fg mt-1">
            {mode === "login" ? "Sign in to see today's breakouts." : "Free account — start scanning instantly."}
          </p>
        </div>

        <button
          type="button"
          onClick={googleLogin}
          data-testid="auth-google-button"
          className="w-full flex items-center justify-center gap-3 px-4 py-3 border border-subtle rounded-sm bg-surface text-primary-fg hover:bg-surface-hover hover:border-focus transition-all text-sm font-medium"
        >
          <GoogleLogo size={18} weight="bold" />
          Continue with Google
        </button>

        <div className="my-6 flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
          <div className="flex-1 h-px bg-white/10" />
          or email
          <div className="flex-1 h-px bg-white/10" />
        </div>

        <form onSubmit={submit} className="space-y-3">
          {mode === "signup" && (
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">Name</span>
              <div className="relative mt-1">
                <User size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
                <input
                  data-testid="auth-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  className="w-full pl-9 pr-3 py-2.5 bg-app border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition"
                />
              </div>
            </label>
          )}
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg">Email</span>
            <div className="relative mt-1">
              <EnvelopeSimple size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
              <input
                data-testid="auth-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="w-full pl-9 pr-3 py-2.5 bg-app border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition font-mono text-sm"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center justify-between">
              <span>Password</span>
              {mode === "login" && (
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-pos hover:underline normal-case tracking-normal text-[11px]"
                >
                  Forgot password?
                </Link>
              )}
            </span>
            <div className="relative mt-1">
              <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-fg" />
              <input
                data-testid="auth-password-input"
                type={showPassword ? "text" : "password"}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                minLength={mode === "signup" ? 8 : 6}
                className="w-full pl-9 pr-10 py-2.5 bg-app border border-subtle rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition font-mono text-sm"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                data-testid="auth-password-toggle"
                aria-label={showPassword ? "Hide password" : "Show password"}
                title={showPassword ? "Hide password" : "Show password"}
                className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-muted-fg hover:text-pos transition rounded-sm"
              >
                {showPassword ? <EyeSlash size={16} weight="bold" /> : <Eye size={16} weight="bold" />}
              </button>
            </div>
            {mode === "signup" && (
              <span className="text-[10px] font-mono text-muted-fg mt-1 block">
                8+ chars · letter + number
              </span>
            )}
          </label>

          {mode === "signup" && (
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-muted-fg flex items-center gap-2">
                Referral code
                <span className="text-muted-fg/70 normal-case">(optional · 5 bonus credits)</span>
              </span>
              <div className="relative mt-1">
                <input
                  data-testid="auth-referral-input"
                  value={referralCode}
                  onChange={(e) => setReferralCode(e.target.value.toUpperCase().replace(/\s/g, "").slice(0, 12))}
                  placeholder="e.g. ABC12345"
                  className={
                    "w-full px-3 py-2.5 bg-app border rounded-sm text-primary-fg placeholder:text-muted-fg focus:border-focus transition font-mono tracking-[0.2em] text-sm " +
                    (referralValid === true ? "border-pos" : referralValid === false ? "border-neg" : "border-subtle")
                  }
                />
                {referralValid === true && (
                  <div className="text-[10px] font-mono text-pos mt-1" data-testid="referral-valid">
                    ✓ Valid · referred by {referrerName}
                  </div>
                )}
                {referralValid === false && (
                  <div className="text-[10px] font-mono text-neg mt-1" data-testid="referral-invalid">
                    ✗ Invalid code
                  </div>
                )}
              </div>
            </label>
          )}

          {err && (
            <div data-testid="auth-error" className="text-neg text-sm bg-neg-soft border border-neg-soft px-3 py-2 rounded-sm">
              {err}
            </div>
          )}

          <button
            type="submit"
            disabled={busy}
            data-testid="auth-submit-button"
            className="w-full mt-2 px-4 py-3 rounded-sm font-medium hover:opacity-90 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
          >
            {busy ? "Please wait…" : mode === "login" ? "Sign in" : "Create account"}
            {!busy && <ArrowRight size={16} weight="bold" />}
          </button>
        </form>

        <div className="mt-6 text-center text-sm text-secondary-fg">
          {mode === "login" ? (
            <>
              New here?{" "}
              <button
                data-testid="auth-switch-signup"
                type="button"
                className="text-primary-fg underline underline-offset-4 hover:text-pos"
                onClick={() => setMode("signup")}
              >
                Create an account
              </button>
            </>
          ) : (
            <>
              Have an account?{" "}
              <button
                data-testid="auth-switch-login"
                type="button"
                className="text-primary-fg underline underline-offset-4 hover:text-pos"
                onClick={() => setMode("login")}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </motion.div>
    </div>
  );
}
